import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config';

// Links a las tiendas (la app ya está publicada en ambas)
const APP_STORE_URL = 'https://apps.apple.com/app/id6758485409';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.futbolero.app';

type MobileOS = 'ios' | 'android' | null;

const detectMobileOS = (): MobileOS => {
  if (typeof window === 'undefined') return null;
  const ua = window.navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return null;
};

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.36 12.78c.02 2.48 2.18 3.31 2.2 3.32-.02.06-.34 1.18-1.13 2.33-.68 1-1.39 2-2.5 2.02-1.1.02-1.45-.65-2.7-.65-1.26 0-1.65.63-2.69.67-1.08.04-1.9-1.08-2.59-2.08-1.4-2.04-2.48-5.76-1.04-8.28.72-1.25 2-2.04 3.39-2.06 1.06-.02 2.06.71 2.7.71.65 0 1.86-.88 3.14-.75.53.02 2.04.22 3 1.62-.08.05-1.79 1.05-1.78 3.13M14.3 5.6c.57-.69.95-1.65.85-2.6-.82.03-1.81.55-2.4 1.23-.53.61-.99 1.58-.87 2.51.91.07 1.85-.46 2.42-1.14"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3.6 2.4 13 12 3.6 21.6c-.37-.2-.6-.6-.6-1.06V3.46c0-.46.23-.86.6-1.06Z" fill="#fff"/>
    <path d="M16.3 8.9 13 12l3.3 3.1 3.6-2.06c.62-.36.62-1.32 0-1.68L16.3 8.9Z" fill="#fff"/>
    <path d="M13 12 3.6 2.4c.1-.05.2-.09.32-.11L16.3 8.9 13 12Z" fill="#fff"/>
    <path d="M13 12 3.92 21.71c-.11-.02-.22-.06-.32-.11L13 12Z" fill="#fff"/>
  </svg>
);

interface GroupPreview {
  group: {
    id: string;
    name: string;
    description?: string | null;
    memberCount: number;
    creator: { id: string; name: string; avatar?: string | null };
  };
  nextMatch: {
    id: string;
    date: string;
    time: string;
    location: string;
    playersJoined: number;
    maxPlayers: number;
  } | null;
  token: string;
}

const GroupJoinPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();

  const [preview, setPreview] = useState<GroupPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [requested, setRequested] = useState(false);
  const [continueInBrowser, setContinueInBrowser] = useState(false);
  const [os] = useState<MobileOS>(() => detectMobileOS());
  const isMobile = os !== null;

  const storeUrl = os === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;

  // Llevar a la tienda correspondiente para bajar la app
  const goToStore = () => {
    window.location.href = storeUrl;
  };

  // "Ya tengo la app": intentar abrir el deep link y, si no se abre (app no
  // instalada), caer a la tienda en vez de romper con un error de navegador.
  const openInApp = () => {
    if (!token) return;
    let fellBack = false;
    const fallback = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        fellBack = true;
        window.location.href = storeUrl;
      }
    }, 1500);
    const onHide = () => {
      if (!fellBack) window.clearTimeout(fallback);
    };
    document.addEventListener('visibilitychange', onHide, { once: true });
    window.location.href = `futbolero://join-group/${token}`;
  };

  useEffect(() => {
    if (!token) {
      setError('Link inválido');
      setLoading(false);
      return;
    }

    fetch(getApiUrl(`/api/groups/join-preview/${token}`))
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setPreview(data);
        }
      })
      .catch(() => setError('Error al cargar la información del grupo'))
      .finally(() => setLoading(false));
  }, [token]);

  // Si el usuario acaba de autenticarse y hay un token pendiente, solicitar automáticamente
  useEffect(() => {
    const pendingToken = localStorage.getItem('pendingGroupToken');
    if (user && pendingToken && pendingToken === token && preview && !requested) {
      localStorage.removeItem('pendingGroupToken');
      handleJoin(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview, token]);

  const handleJoin = async (auto = false) => {
    if (!user) {
      localStorage.setItem('pendingGroupToken', token!);
      localStorage.setItem('redirectAfterAuth', `/join-group/${token}`);
      loginWithGoogle();
      return;
    }

    if (joining) return;
    setJoining(true);

    try {
      const res = await fetch(getApiUrl(`/api/groups/join/${token}`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Error al solicitar unirse al grupo');
        return;
      }

      if (data.alreadyMember) {
        navigate(`/groups/${data.groupId}`);
        return;
      }

      setRequested(true);
    } catch {
      setError('Error de conexión');
    } finally {
      setJoining(false);
    }
  };

  const formatDate = (date: string, time: string) => {
    try {
      const [year, month, day] = date.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      const dayName = d.toLocaleDateString('es-AR', { weekday: 'long' });
      const dayNum = d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });
      return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNum} · ${time}`;
    } catch {
      return `${date} ${time}`;
    }
  };

  const initials = (name?: string) =>
    (name || '?')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() || '')
      .join('');

  if (loading) {
    return (
      <div className="gj-page">
        <Styles />
        <div className="gj-glow" />
        <div className="gj-loading">
          <div className="gj-ball">⚽</div>
          <p className="gj-loading-text">Cargando invitación…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gj-page">
        <Styles />
        <div className="gj-glow" />
        <div className="gj-card gj-enter" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>😕</div>
          <h2 className="gj-group-name" style={{ fontSize: 22 }}>Link no disponible</h2>
          <p className="gj-muted" style={{ margin: '4px 0 24px' }}>{error}</p>
          <button type="button" onClick={() => navigate('/')} className="gj-btn-outline">
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  const { group, nextMatch } = preview;

  if (requested) {
    return (
      <div className="gj-page">
        <Styles />
        <div className="gj-glow" />
        <div className="gj-card gj-enter" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div className="gj-check">✓</div>
          <h2 className="gj-group-name" style={{ fontSize: 22, marginTop: 18 }}>¡Solicitud enviada!</h2>
          <p className="gj-muted" style={{ margin: '6px 0 0', lineHeight: 1.5 }}>
            Pediste sumarte a <strong style={{ color: 'rgb(var(--color-text-rgb))' }}>{group.name}</strong>.
            Te avisamos apenas el organizador la apruebe.
          </p>
        </div>
      </div>
    );
  }

  const pct = nextMatch
    ? Math.min(100, Math.round((nextMatch.playersJoined / Math.max(1, nextMatch.maxPlayers)) * 100))
    : 0;
  const isFull = nextMatch ? nextMatch.playersJoined >= nextMatch.maxPlayers : false;

  return (
    <div className="gj-page">
      <Styles />
      <div className="gj-glow" />

      <div className="gj-shell">
        <div className="gj-logo">⚽ Futbolero</div>

        <div className="gj-card gj-enter">
          {/* Portada con gradiente */}
          <div className="gj-cover">
            <div className="gj-cover-grid" />
          </div>

          {/* Avatar del organizador */}
          <div className="gj-avatar-wrap">
            {group.creator.avatar ? (
              <img src={group.creator.avatar} alt="" className="gj-avatar" />
            ) : (
              <div className="gj-avatar gj-avatar-fallback">{initials(group.creator.name)}</div>
            )}
            <span className="gj-avatar-badge">⚽</span>
          </div>

          <div className="gj-body">
            <p className="gj-invited">
              <strong>{group.creator.name}</strong> te invitó a su grupo
            </p>
            <h1 className="gj-group-name">{group.name}</h1>

            {group.description && <p className="gj-desc">{group.description}</p>}

            {/* Chips */}
            <div className="gj-chips">
              <span className="gj-chip">
                <span className="gj-chip-ico">👥</span>
                {group.memberCount} {group.memberCount === 1 ? 'jugador' : 'jugadores'}
              </span>
              {nextMatch && (
                <span className="gj-chip">
                  <span className="gj-chip-ico">📅</span>
                  Próximo partido
                </span>
              )}
            </div>

            {/* Próximo partido */}
            {nextMatch && (
              <div className="gj-match">
                <div className="gj-match-head">⚽ Próximo partido</div>
                <div className="gj-match-date">{formatDate(nextMatch.date, nextMatch.time)}</div>
                <div className="gj-match-loc">📍 {nextMatch.location}</div>
                <div className="gj-progress">
                  <div
                    className="gj-progress-bar"
                    style={{ width: `${pct}%`, background: isFull ? '#FF6B6B' : 'rgb(var(--color-primary-rgb))' }}
                  />
                </div>
                <div className="gj-match-players">
                  <strong style={{ color: isFull ? '#FF6B6B' : 'rgb(var(--color-primary-rgb))' }}>
                    {nextMatch.playersJoined}/{nextMatch.maxPlayers}
                  </strong>{' '}
                  {isFull ? 'completo · podés anotarte de suplente' : 'confirmados'}
                </div>
              </div>
            )}

            {/* CTAs */}
            {isMobile && !continueInBrowser ? (
              <div className="gj-cta">
                <button type="button" onClick={goToStore} className="gj-btn-primary">
                  <span className="gj-store-ico">{os === 'ios' ? <AppleIcon /> : <PlayIcon />}</span>
                  Bajar la app gratis y sumarme
                </button>
                <div className="gj-trust">Gratis · Sin tarjeta · Listo en 1 minuto</div>

                {/* Value props */}
                <div className="gj-feats">
                  <span className="gj-feat"><span>🔔</span>Avisos</span>
                  <span className="gj-feat"><span>⭐</span>Votás MVP</span>
                  <span className="gj-feat"><span>🏆</span>Tabla</span>
                </div>

                <button type="button" onClick={openInApp} className="gj-btn-ghost">
                  Ya tengo Futbolero · Abrir en la app
                </button>

                <div className="gj-divider"><span />o<span /></div>

                <button
                  type="button"
                  onClick={() => setContinueInBrowser(true)}
                  className="gj-btn-outline"
                >
                  Unirme desde el navegador
                </button>
              </div>
            ) : (
              <div className="gj-cta">
                <button
                  type="button"
                  onClick={() => handleJoin()}
                  disabled={joining}
                  className="gj-btn-primary"
                  style={{ opacity: joining ? 0.75 : 1, cursor: joining ? 'progress' : 'pointer' }}
                >
                  {joining ? 'Enviando solicitud…' : '🙌 Solicitar unirme'}
                </button>

                {!user ? (
                  <p className="gj-muted gj-hint">
                    Entrás con Google en menos de 1 minuto. No pedimos tarjeta.
                  </p>
                ) : (
                  <p className="gj-muted gj-hint">
                    Ingresás como <strong style={{ color: 'rgb(var(--color-text-rgb))' }}>{user.name}</strong>
                  </p>
                )}

                {isMobile && (
                  <button type="button" onClick={goToStore} className="gj-btn-ghost">
                    📲 Mejor bajo la app
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="gj-footer">Organizá tus partidos sin perderte en el grupo de WhatsApp</p>
      </div>
    </div>
  );
};

/** Estilos + animaciones (clases para poder usar :hover y keyframes) */
const Styles = () => (
  <style>{`
    .gj-page {
      position: relative;
      min-height: 100vh;
      background:
        radial-gradient(1200px 600px at 50% -10%, rgb(var(--color-primary-rgb) / 0.18), transparent 60%),
        rgb(var(--color-background-rgb));
      display: flex; align-items: flex-start; justify-content: center;
      padding: 28px 16px 48px;
      overflow: hidden;
    }
    .gj-glow {
      position: absolute; top: -160px; left: 50%; transform: translateX(-50%);
      width: 420px; height: 420px; border-radius: 50%;
      background: rgb(var(--color-primary-rgb) / 0.22);
      filter: blur(90px); pointer-events: none;
    }
    .gj-shell { position: relative; width: 100%; max-width: 440px; display: flex; flex-direction: column; align-items: center; }
    .gj-logo {
      font-weight: 800; font-size: 18px; letter-spacing: .2px;
      color: rgb(var(--color-primary-rgb)); margin-bottom: 18px;
    }

    .gj-card {
      position: relative; width: 100%;
      background: rgb(var(--color-surface-rgb));
      border: 1px solid rgb(var(--color-border-rgb));
      border-radius: 26px; overflow: hidden;
      box-shadow: 0 24px 60px rgba(0,0,0,.38);
    }
    .gj-enter { animation: gjIn .5s cubic-bezier(.16,.84,.44,1) both; }
    @keyframes gjIn { from { opacity: 0; transform: translateY(16px) scale(.98); } to { opacity: 1; transform: none; } }

    .gj-cover {
      height: 96px;
      background: linear-gradient(135deg, rgb(var(--color-primary-rgb)), rgb(var(--color-primaryDark-rgb)));
      position: relative;
    }
    .gj-cover-grid {
      position: absolute; inset: 0;
      background-image:
        linear-gradient(rgba(255,255,255,.12) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,.12) 1px, transparent 1px);
      background-size: 26px 26px;
      mask-image: linear-gradient(180deg, rgba(0,0,0,.6), transparent);
      -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,.6), transparent);
    }

    .gj-avatar-wrap { position: relative; width: 92px; margin: -46px auto 0; }
    .gj-avatar {
      width: 92px; height: 92px; border-radius: 50%;
      object-fit: cover; display: block;
      border: 4px solid rgb(var(--color-surface-rgb));
      box-shadow: 0 8px 22px rgba(0,0,0,.4);
      background: rgb(var(--color-background-rgb));
    }
    .gj-avatar-fallback {
      display: flex; align-items: center; justify-content: center;
      font-size: 30px; font-weight: 800; color: #062;
      background: linear-gradient(135deg, rgb(var(--color-primary-rgb)), rgb(var(--color-primaryDark-rgb)));
    }
    .gj-avatar-badge {
      position: absolute; right: 0; bottom: 2px;
      width: 30px; height: 30px; border-radius: 50%;
      background: rgb(var(--color-surface-rgb));
      border: 2px solid rgb(var(--color-surface-rgb));
      box-shadow: 0 2px 6px rgba(0,0,0,.3);
      display: flex; align-items: center; justify-content: center; font-size: 15px;
    }

    .gj-body { padding: 14px 22px 26px; text-align: center; }
    .gj-invited { color: rgb(var(--color-textSecondary-rgb)); font-size: 14px; margin: 0 0 4px; }
    .gj-invited strong { color: rgb(var(--color-text-rgb)); }
    .gj-group-name {
      color: rgb(var(--color-text-rgb)); font-size: 27px; font-weight: 800;
      margin: 0; line-height: 1.18; letter-spacing: -.3px;
    }
    .gj-desc { color: rgb(var(--color-textSecondary-rgb)); font-size: 14px; margin: 10px 0 0; line-height: 1.5; }
    .gj-muted { color: rgb(var(--color-textSecondary-rgb)); }

    .gj-chips { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin: 16px 0 4px; }
    .gj-chip {
      display: inline-flex; align-items: center; gap: 6px;
      background: rgb(var(--color-primary-rgb) / 0.12);
      color: rgb(var(--color-text-rgb));
      border: 1px solid rgb(var(--color-primary-rgb) / 0.25);
      padding: 6px 12px; border-radius: 999px; font-size: 13px; font-weight: 600;
    }
    .gj-chip-ico { font-size: 13px; }

    .gj-match {
      margin-top: 18px; padding: 16px;
      background: rgb(var(--color-background-rgb) / 0.55);
      border: 1px solid rgb(var(--color-border-rgb));
      border-radius: 16px; text-align: left;
    }
    .gj-match-head {
      font-size: 11px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
      color: rgb(var(--color-primary-rgb)); margin-bottom: 8px;
    }
    .gj-match-date { font-size: 15px; font-weight: 700; color: rgb(var(--color-text-rgb)); }
    .gj-match-loc { font-size: 13px; color: rgb(var(--color-textSecondary-rgb)); margin-top: 2px; }
    .gj-progress {
      margin: 12px 0 6px; height: 7px; border-radius: 999px;
      background: rgb(var(--color-border-rgb)); overflow: hidden;
    }
    .gj-progress-bar { height: 100%; border-radius: 999px; transition: width .6s ease; }
    .gj-match-players { font-size: 12.5px; color: rgb(var(--color-textSecondary-rgb)); }

    .gj-cta { margin-top: 22px; display: flex; flex-direction: column; }
    .gj-btn-primary {
      width: 100%; padding: 16px; border: none; border-radius: 15px;
      background: linear-gradient(135deg, rgb(var(--color-primary-rgb)), rgb(var(--color-primaryDark-rgb)));
      color: #04210f; font-size: 16px; font-weight: 800; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center; gap: 10px;
      box-shadow: 0 10px 24px rgb(var(--color-primary-rgb) / 0.32);
      transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
    }
    .gj-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 14px 30px rgb(var(--color-primary-rgb) / 0.42); filter: brightness(1.03); }
    .gj-btn-primary:active { transform: translateY(0); }
    .gj-store-ico { display: inline-flex; color: #04210f; }

    .gj-trust { margin-top: 9px; font-size: 12px; color: rgb(var(--color-textSecondary-rgb)); }

    .gj-feats { display: flex; gap: 8px; justify-content: center; margin: 16px 0 6px; }
    .gj-feat {
      flex: 1; min-width: 0;
      display: inline-flex; flex-direction: column; align-items: center; gap: 4px;
      padding: 10px 4px; border-radius: 12px;
      background: rgb(var(--color-background-rgb) / 0.5);
      border: 1px solid rgb(var(--color-border-rgb));
      font-size: 12px; font-weight: 600; color: rgb(var(--color-textSecondary-rgb));
    }
    .gj-feat span { font-size: 18px; }

    .gj-btn-ghost {
      width: 100%; padding: 12px; margin-top: 12px; background: transparent; border: none;
      color: rgb(var(--color-primary-rgb)); font-size: 14px; font-weight: 700; cursor: pointer;
      border-radius: 10px; transition: background .12s ease;
    }
    .gj-btn-ghost:hover { background: rgb(var(--color-primary-rgb) / 0.1); }

    .gj-divider { display: flex; align-items: center; gap: 12px; margin: 12px 2px; color: rgb(var(--color-textSecondary-rgb)); font-size: 12px; }
    .gj-divider span { flex: 1; height: 1px; background: rgb(var(--color-border-rgb)); }

    .gj-btn-outline {
      width: 100%; padding: 14px; border-radius: 14px;
      background: transparent; color: rgb(var(--color-text-rgb));
      border: 1px solid rgb(var(--color-border-rgb));
      font-size: 14.5px; font-weight: 600; cursor: pointer;
      transition: border-color .12s ease, background .12s ease;
    }
    .gj-btn-outline:hover { border-color: rgb(var(--color-primary-rgb) / 0.6); background: rgb(var(--color-primary-rgb) / 0.06); }

    .gj-hint { font-size: 13px; text-align: center; margin: 12px 0 0; line-height: 1.5; }

    .gj-footer { color: rgb(var(--color-textSecondary-rgb)); font-size: 12px; margin: 22px 0 0; text-align: center; }

    /* Loading */
    .gj-loading { display: flex; flex-direction: column; align-items: center; gap: 14px; margin-top: 28vh; }
    .gj-ball { font-size: 40px; animation: gjBounce 1s infinite ease-in-out; }
    @keyframes gjBounce { 0%,100% { transform: translateY(0);} 50% { transform: translateY(-12px);} }
    .gj-loading-text { color: rgb(var(--color-textSecondary-rgb)); font-size: 14px; }

    /* Success check */
    .gj-check {
      width: 72px; height: 72px; border-radius: 50%; margin: 0 auto;
      background: linear-gradient(135deg, rgb(var(--color-primary-rgb)), rgb(var(--color-primaryDark-rgb)));
      color: #04210f; font-size: 38px; font-weight: 900;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 26px rgb(var(--color-primary-rgb) / 0.4);
      animation: gjPop .45s cubic-bezier(.16,.84,.44,1) both;
    }
    @keyframes gjPop { 0% { transform: scale(.4); opacity: 0;} 60% { transform: scale(1.1);} 100% { transform: scale(1); opacity: 1;} }
  `}</style>
);

export default GroupJoinPage;
